"use client";

// WO-GF-001-P1B-S11 (RW-13) — HUD SHRINK-ON-SCROLL.
//
// Founder re-walk RW-13: "HUD: wrong size vs live + missing shrink-on-scroll." §0f live-derived (production
// snowbus.com /mountain-whistler, Playwright @1440×900, read-only) by measuring the chrome at scrollY 0 and
// scrollY 800:
//
//   element                     at top      scrolled
//   header (MuiAppBar)          h 140       h 140      ← unchanged; the AppBar reserves the space
//   .MuiToolbar-root            h 122       h 74       ← THIS is what shrinks (−48)
//   mode-nav box (css-fmy3cc)   h 90        h 58       ← −32
//   logo / cart boxes           top 16      top 8      ← −8 vertical offset
//
// Live drives it by swapping emotion classes (css-166albc→css-1mm5111, css-b3n6tb→css-fki6i2) — i.e. a
// styled-component variant, not a class the mirror carries. The capture is frozen in the AT-TOP state, so we
// reproduce the EFFECT on the captured DOM: past the threshold we set the derived heights/offsets, and we
// restore them at the top.
//
// PIXEL-SAFE BY CONSTRUCTION: the gate screenshots at scrollY 0, where this layer writes nothing (it only
// applies styles after the threshold is crossed, and clears them on the way back). Nothing is mutated on
// mount. Desktop only (F30) — mounted alongside the other hub chrome.
//
// Harness hooks: data-hud-shrink (the layer), data-hud-shrunk="true|false" (on the toolbar).

import { useEffect } from "react";

// ---------------------------------------------------------------------------------------------------
// S12 · RW2-12 — the S11 shrink was INCOMPLETE, and the founder was right to reject it.
//
// S11 matched the toolbar HEIGHT (122→74) and stopped there, so the height gate passed while the HUD still
// looked wrong. Side-by-side live-vs-build capture at 1440×900 (evidence: captures/evidence-s12/
// hud-whistler-hub-{live,build}-{rest,scrolled}.png) shows the at-rest states are pixel-alike, but the
// SCROLLED states clearly are not: live collapses to an icons-only pill with a small badge, while the build
// kept full-size text labels and a full-size badge inside a shorter bar.
//
// Re-measured on live (read-only, 2026-07-22, /mountain-whistler @1440×900, scrollY 0 → 600):
//   element                       at top        scrolled
//   .MuiToolbar-root              h 122         h 74        ← S11 had this right
//   mode-nav box                  h 90          h 58        ← S11 had this right
//   mode item (MuiButton-root)    112 × 58      90 × 42     ← MISSING in S11
//   item label (p.MuiTypography)  h 18          h 0         ← MISSING in S11 (collapses, display stays block)
//   mountain badge img            120 × 60      80 × 40     ← MISSING in S11
//
// The build's AT-REST geometry already matches live on every one of these (112×58 / 18 / 120×60 measured),
// so only the collapsed state needed fixing — which is exactly why a height-only check called it done.
// ---------------------------------------------------------------------------------------------------

// ---------------------------------------------------------------------------------------------------
// S16 · F103 — the founder re-walked this and was right again. Re-measured live vs build @1440×900,
// /mountain-whistler and /faq, scrollY 0 → 600 (evidence: the S16 probe rows in BEHAVIOR-LEDGER).
//
// AT REST the build matches live on every number (toolbar 122, rows y=16, logo 24,16 / profile 1376,24,
// badge 351,31 120×60, items 112/100/100/100/100 @ min-width 100). SCROLLED, three things were wrong:
//
//   (1) THE ROWS NEVER MOVED UP. Live drives the whole collapse off the TOOLBAR'S VERTICAL PADDING —
//       16px → 8px — which lifts all three rows (logo box / mode-nav / cart+profile box) from y=16 to
//       y=8. S11/S12 shrank heights but never touched the offsets, so the build's rows stayed at y=16
//       inside a 74px bar: the nav row's bottom edge landed flush at 74 with no breathing room, and the
//       logo and profile sat 8px low. The header's own outer height (140) is unchanged on live — the
//       AppBar reserves the space either way — so this is purely the toolbar's own box.
//
//   (2) THE ITEMS WERE PINNED TO A UNIFORM 90px. S12 measured "marketplace" at 90 and applied it to all
//       five. Live keeps each item's INTRINSIC width once the label collapses: 90 / 74 / 53 / 48 / 69
//       (they track word length; the at-rest min-width:100 is dropped). Build 5×90 = 450 vs live 334 —
//       a 116px difference which, centred, pushed the mountain badge 58px left of live (404 vs 462).
//       That arithmetic closes exactly, which is how this was identified rather than guessed.
//
//   (3) THE RESTORE PATH RE-ASSERTED 112px ON EVERY ITEM. At rest only "marketplace" is 112; the other
//       four are 100. So scrolling down and back up left the nav permanently 48px wider and shifted the
//       badge — i.e. "things that shouldn't move are moving", visible only after a scroll round-trip,
//       which is why no gate caught it (the pixel gate only ever shoots scrollY 0). The restore now puts
//       back each element's OWN inline style, snapshotted just before the first write — not a set of
//       re-asserted numbers, and not a blanket clear either: other chrome layers write inline styles to
//       these same elements (ContentHudLayer pins the content pill to height:60px), and clearing them
//       brought the content bar back at 104 instead of 116.
//
// Live's arithmetic — toolbar height = mode-nav row + 16 — was verified on BOTH live surfaces
// (hub 58+16=74, content 60+16=76). So the collapse is now CONTENT-DRIVEN: collapse the children, let
// the row size itself, then set the bar to row+16. Nothing here hardcodes a per-surface height, which
// is what lets the same layer serve the content pages (see the mount note in page.tsx).
//
// The hamburger: live's HUD has NO hamburger at either scroll position, on the hub or on content pages
// (measured, hamburgerCount=0 both states) — it exists only in the home header, which is not sticky on
// live and simply scrolls away (build identical). So "shrink but KEEP the hamburger" has no live
// counterpart to port; it is the same open question as F100 and is recorded there, not invented here.
// ---------------------------------------------------------------------------------------------------

// live's measured values
const PAD_TOP = 16; // toolbar vertical padding at rest
const PAD_SHRUNK = 8; // …and past the threshold — this is what lifts every row 16 → 8
const ITEM_SHRUNK_H = 42;
// live's collapsed mode item: padding 6px/12px → 4px/1.2px and gap 4px → 0. This is what actually
// produces the intrinsic widths (label width is byte-identical on both — 85.875px for "marketplace" —
// so live's 90 vs the build's 112 was exactly the 2 × 12px of padding it sheds).
const ITEM_PAD_SHRUNK = "4px 1.2px";
const ITEM_GAP_SHRUNK = "0px";
const LABEL_SHRUNK = 0;
const BADGE_SHRUNK = { w: 80, h: 40 };
const BADGE_BOX_SHRUNK = { w: 80, h: 42 };
const INNER_PAD_SHRUNK = "0px 8px"; // live swaps css-b3n6tb (0 16px) → css-fki6i2 (0 8px)
const CHEVRON_TOP = 16;
const CHEVRON_SHRUNK = 12;
const THRESHOLD = 80; // px of scroll before the shrink engages

export default function HudShrinkLayer() {
  useEffect(() => {
    const header = document.querySelector<HTMLElement>("header");
    if (!header) return;
    const toolbar = header.querySelector<HTMLElement>(".MuiToolbar-root");
    if (!toolbar) return;

    // the mode-nav box = the widest direct MuiBox child of the toolbar (live: css-fmy3cc, w 1104).
    const boxes = Array.from(
      toolbar.querySelectorAll<HTMLElement>(":scope > .MuiBox-root"),
    );
    const nav =
      boxes
        .slice()
        .sort(
          (a, b) => b.getBoundingClientRect().width - a.getBoundingClientRect().width,
        )[0] ?? null;

    // RW2-12 — the pieces live also shrinks. The mode items are <a> on the 30 landings (SiteHeaderHub) and
    // <button> on the ~361 sub-pages (HubChromeLayer), so match on the shared MUI class rather than the tag.
    const items = Array.from(
      toolbar.querySelectorAll<HTMLElement>(".MuiButton-root"),
    );
    const labels = items
      .map((it) => it.querySelector<HTMLElement>("p.MuiTypography-root"))
      .filter((e): e is HTMLElement => !!e);
    // the per-mountain badge (live: 120×60 → 80×40). Scoped to its captured box so the SnowBus mark, the
    // chevron, the cart and the profile icon — none of which live resizes — are left alone.
    const badge = toolbar.querySelector<HTMLElement>(
      ".css-btdhl8 img, .css-btdhl8 > img",
    );
    // F103 — the badge's BOX shrinks with it on live (120×60 → 80×42); S12 resized only the <img>, so the
    // box kept reserving 120×60 and held the row wider than live's.
    const badgeBox = badge?.closest<HTMLElement>(".MuiBox-root") ?? null;
    // F103 — the mode-nav's inner container: live swaps its emotion class on scroll, which drops the
    // horizontal padding 16 → 8. Height is left to follow its (now collapsed) children.
    const inner = nav?.firstElementChild as HTMLElement | null;
    // F103 — the 16 → 8 shed is not just the toolbar's: every wrapper between a mode item and the nav row
    // carries the same 16px vertical padding, and live's class swap drops all of them together. Collect
    // them by MEASURING (padding-top === 16px) rather than naming emotion classes, which are hashed and
    // differ between the landing header and the captured sub-page markup. Measurement only — no writes,
    // so the at-rest DOM is still untouched on mount.
    const padHosts: HTMLElement[] = [];
    for (const it of items) {
      let el = it.parentElement as HTMLElement | null;
      while (el && el !== nav && el !== toolbar) {
        if (
          !padHosts.includes(el) &&
          getComputedStyle(el).paddingTop === `${PAD_TOP}px`
        ) {
          padHosts.push(el);
        }
        el = el.parentElement as HTMLElement | null;
      }
    }
    // the change-mountain chevron shrinks too (live: 16×16 → 12×12). The SnowBus mark (35×40), cart badge
    // and profile icon are UNCHANGED on live at both scroll positions, so they are deliberately not touched.
    const chevron = toolbar.querySelector<HTMLElement>(
      'img[alt="Change mountain"]',
    );

    let shrunk: boolean | null = null; // null = never applied → at-rest DOM untouched

    // F103 — restoring by CLEARING inline styles is wrong: this layer is not the only one that writes
    // them. On the content pages ContentHudLayer sets the "SELECT YOUR MOUNTAIN" pill to height:60px
    // inline, and blanking it left the pill at its CSS 48 — the bar came back 104 instead of 116 after a
    // scroll round-trip. So snapshot each element's OWN inline style and put exactly that back.
    //
    // The snapshot is taken lazily, immediately before the first write, NOT on mount: the other chrome
    // layers hydrate independently and mount order is not guaranteed, so at mount their styles may not be
    // on the element yet. By the first scroll past the threshold the page has settled.
    const touched = () => [
      toolbar,
      ...(nav ? [nav] : []),
      ...(inner ? [inner] : []),
      ...padHosts,
      ...items,
      ...labels,
      ...(badge ? [badge] : []),
      ...(badgeBox ? [badgeBox] : []),
      ...(chevron ? [chevron] : []),
    ];
    let saved: Map<HTMLElement, string> | null = null;
    const snapshot = () => {
      if (saved) return;
      saved = new Map();
      for (const el of touched()) saved.set(el, el.style.cssText);
    };
    const restoreAll = () => {
      if (!saved) return;
      for (const [el, css] of saved) el.style.cssText = css;
    };

    const apply = (next: boolean) => {
      if (next === shrunk) return;
      shrunk = next;
      toolbar.style.transition =
        "height .18s ease, min-height .18s ease, padding .18s ease";
      if (nav) nav.style.transition = "height .18s ease";
      for (const h of padHosts) h.style.transition = "padding .18s ease";
      for (const it of items) it.style.transition = "width .18s ease, height .18s ease";
      for (const l of labels)
        l.style.transition = "height .18s ease, opacity .18s ease";
      if (badge) badge.style.transition = "width .18s ease, height .18s ease";
      if (badgeBox) badgeBox.style.transition = "width .18s ease, height .18s ease";
      if (chevron) chevron.style.transition = "width .18s ease, height .18s ease";

      if (next) {
        snapshot(); // capture every touched element's own inline style before the first write
        // (1) the offsets — live's whole collapse hangs off this one pair of values.
        toolbar.style.paddingTop = `${PAD_SHRUNK}px`;
        toolbar.style.paddingBottom = `${PAD_SHRUNK}px`;
        for (const it of items) {
          // (2) intrinsic width, not a uniform pin: drop the at-rest min-width and let each item size to
          // its own (label-collapsed) content, which is what live does.
          it.style.width = "auto";
          it.style.minWidth = "0";
          it.style.height = `${ITEM_SHRUNK_H}px`;
          // MuiButton carries its own min-height, which floors the collapsed item at 48px unless it is
          // overridden too (measured: 48 vs live's 42).
          it.style.minHeight = `${ITEM_SHRUNK_H}px`;
          it.style.padding = ITEM_PAD_SHRUNK;
          it.style.gap = ITEM_GAP_SHRUNK;
        }
        for (const h of padHosts) {
          h.style.paddingTop = `${PAD_SHRUNK}px`;
          h.style.paddingBottom = `${PAD_SHRUNK}px`;
        }
        for (const l of labels) {
          // live collapses the label to zero height (it stays display:block) — so height + overflow, not
          // display:none, which would also drop it out of the flex measurement live still performs. Its
          // WIDTH still counts, which is precisely what gives each item its intrinsic collapsed width.
          l.style.height = `${LABEL_SHRUNK}px`;
          l.style.overflow = "hidden";
          l.style.opacity = "0";
          l.style.margin = "0";
        }
        if (badge) {
          badge.style.width = `${BADGE_SHRUNK.w}px`;
          badge.style.height = `${BADGE_SHRUNK.h}px`;
        }
        if (badgeBox) {
          badgeBox.style.width = `${BADGE_BOX_SHRUNK.w}px`;
          badgeBox.style.height = `${BADGE_BOX_SHRUNK.h}px`;
        }
        if (chevron) {
          chevron.style.width = `${CHEVRON_SHRUNK}px`;
          chevron.style.height = `${CHEVRON_SHRUNK}px`;
        }
        if (inner) {
          inner.style.height = "auto";
          inner.style.padding = INNER_PAD_SHRUNK;
        }
        if (nav) nav.style.height = "auto";
        // (3) live's own arithmetic — bar = mode-nav row + its padding — is simply what a content-sized
        // box already does, so the height is deliberately NOT written. Measuring the row and pinning the
        // result would read it mid-transition (the padding is animating), which is exactly how an
        // earlier cut of this fix landed on 90 instead of 74. Letting it size itself is both correct and
        // self-adapting for the taller RW3-8 content bar.
      } else {
        restoreAll();
      }
      toolbar.setAttribute("data-hud-shrunk", String(next));
    };

    const onScroll = () => {
      const y = window.scrollY || document.documentElement.scrollTop || 0;
      if (y > THRESHOLD) apply(true);
      else if (shrunk !== null) apply(false); // only ever un-shrink something we shrank
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll(); // honour a restored scroll position (deep link / back-nav)
    return () => {
      window.removeEventListener("scroll", onScroll);
      // leave the DOM exactly as the other layers left it
      restoreAll();
      toolbar.style.transition = "";
      toolbar.removeAttribute("data-hud-shrunk");
      if (nav) nav.style.transition = "";
      if (inner) inner.style.transition = "";
      for (const h of padHosts) h.style.transition = "";
      for (const it of items) it.style.transition = "";
      for (const l of labels) l.style.transition = "";
      if (badge) badge.style.transition = "";
      if (badgeBox) badgeBox.style.transition = "";
      if (chevron) chevron.style.transition = "";
    };
  }, []);

  return <span data-hud-shrink hidden />;
}
